import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import walletService from "../../../services/WalletService";
import { emitCheckoutEvent } from "../../../services/checkoutEvents";
import { storage } from "../../../storageconstants";
import { debugLog } from "../../../utils";
import {
  setCheckoutBreakdownTotal,
  setCheckoutFeeBreakdownEnabled,
} from "../../../utils/checkoutFeeBreakdown";

/** Same visual as token row :hover (Tailwind payment-options token item hover tokens) */
const PASSIVE_ROW_CLASSES = [
  "bg-thm-selector-asset-selector-passive-bg",
  "border-thm-selector-asset-selector-passive-border-color",
];

const SELECTED_ROW_CLASSES = [
  "bg-thm-selector-asset-selector-active-bg",
  "border-thm-selector-asset-selector-active-border-color",
];

const SELECTED_CARD_CLASSES = [
  "border-thm-selector-active-border-color",
  "border-2",
];

const OPTION_ICON_CIRCLE_DEFAULT_CLASSES = ["bg-thm-selector-passive-icon-bg", "shadow-none"];
const OPTION_ICON_CIRCLE_SELECTED_CLASSES = ["bg-thm-selector-passive-icon-bg", "shadow-none"];

const OPTION_ICON_DEFAULT_CLASSES = ["text-thm-selector-passive-icon-color"];
const OPTION_ICON_SELECTED_CLASSES = ["text-thm-selector-passive-icon-color"];

function clearAssetRowSelection(row) {
  if (!row) return;
  row.classList.remove(...SELECTED_ROW_CLASSES);
  row.classList.add(...PASSIVE_ROW_CLASSES);
  row.setAttribute("aria-selected", "false");
}

function applyAssetRowSelection(row) {
  if (!row) return;
  row.classList.remove(...PASSIVE_ROW_CLASSES);
  row.classList.add(...SELECTED_ROW_CLASSES);
  row.setAttribute("aria-selected", "true");
}

export default class extends Controller {
  static targets = [
    "optionCard",
    "directTransferPanel",
    "assetRow",
    "continueButton",
    "tokenSearch",
    "tokenList",
  ];

  static values = {
    code: String,
    session: String,
  };

  connect() {
    sessionStorage.setItem(storage.THIRD_PARTY_BACK_TARGET, "payment-options");

    // Browser back restores a cached page with stale Onboard state.
    // Force a one-time hard reload to get a clean runtime.
    if (this.shouldForceReload()) {
      sessionStorage.setItem(storage.TURBO_RESTORE_RELOADED, "1");
      sessionStorage.removeItem(storage.TURBO_RESTORE_PENDING);
      window.location.reload();
      return;
    }
    sessionStorage.removeItem(storage.TURBO_RESTORE_RELOADED);

    this.resetThirdPartyWalletConnection();
    this.selectedOption = null;
    this.selectedRow = null;
    this.hideLoadingScreen();
    if (this.hasOptionCardTarget) {
      this.applySelection(this.initialOptionCard());
    }
    this.restoreSelectionFromStorage();
    this.syncContinueDisabled();
    this.filterTokens();
  }

  /** Statsig `preselected` wins when its card rendered; otherwise Direct Transfer, then the first card. */
  initialOptionCard() {
    return (
      this.optionCardTargets.find((card) => card.dataset.preselected === "true") ||
      this.optionCardTargets.find((card) => card.dataset.paymentOption === "direct-deposit") ||
      this.optionCardTargets[0]
    );
  }

  shouldForceReload() {
    const isRestore = sessionStorage.getItem(storage.TURBO_RESTORE_PENDING) === "1";
    const alreadyReloaded = sessionStorage.getItem(storage.TURBO_RESTORE_RELOADED) === "1";
    return isRestore && !alreadyReloaded;
  }

  async resetThirdPartyWalletConnection() {
    try {
      await walletService.disconnectWallet();
    } catch (error) {
      debugLog("PaymentOptions: failed to disconnect wallet on load", error);
    }
  }

  /** Client-side filter for token rows (symbol + network name). */
  filterTokens() {
    if (!this.hasTokenListTarget) return;
    const q = this.hasTokenSearchTarget
      ? this.tokenSearchTarget.value.trim().toLowerCase()
      : "";
    const rows = this.tokenListTarget.querySelectorAll(":scope > li");
    rows.forEach((li) => {
      const sym = (li.dataset.symbol || "").toLowerCase();
      const net = (li.dataset.networkName || "").toLowerCase();
      const chainId = (li.dataset.chainId || "").toLowerCase();
      const haystack = `${sym} ${net} ${chainId}`.trim();
      const match = !q || haystack.includes(q) || sym.startsWith(q) || net.includes(q);
      li.classList.toggle("hidden", !match);
    });

    if (this.selectedRow?.classList.contains("hidden")) {
      clearAssetRowSelection(this.selectedRow);
      this.selectedRow = null;
      sessionStorage.removeItem(storage.DIRECT_TRANSFER_SYMBOL);
      sessionStorage.removeItem(storage.DIRECT_TRANSFER_NETWORK);
      sessionStorage.removeItem(storage.DIRECT_TRANSFER_ICON);
      sessionStorage.removeItem(storage.DIRECT_TRANSFER_CHAIN_ID);
      this.updateCheckoutPaymentMethod("", "");
      this.syncContinueDisabled();
    }
  }

  selectOption(event) {
    const card = event.currentTarget;
    // Token-row clicks bubble up to the direct-deposit card; re-selecting the
    // current option must not wipe the fee the row just set.
    if (card.dataset.paymentOption === this.selectedOption) return;

    this.applySelection(card);
    this.syncContinueDisabled();
  }

  applySelection(card) {
    if (!card || !card.dataset.paymentOption) return;

    this.selectedOption = card.dataset.paymentOption;
    // Switching options drops any token-specific fee: go back to the summary's
    // original "calculated at payment" state until a concrete token is picked.
    this.resetCheckoutOverviewToPlaceholder();
    if (this.selectedOption === "direct-deposit" && this.selectedRow) {
      this.applyAssetRowToOverview(this.selectedRow);
    }
    this.optionCardTargets.forEach((c) => {
      c.classList.remove(...SELECTED_CARD_CLASSES);
      const radio = c.querySelector(".payment-option-selector-radio");
      const check = radio?.querySelector(".payment-option-selector-radio-icon");
      const optionIconCircle = c.querySelector(".payment-option-circle");
      const optionIcon = c.querySelector(".payment-option-circle-icon");
      if (radio) {
        radio.classList.remove(
          "bg-thm-selector-active-checkbox-color",
          "border-thm-selector-active-checkbox-color",
        );
        radio.classList.add(
          "bg-transparent",
          "border-thm-selector-passive-checkbox-color",
        );
      }

      if (optionIconCircle) {
        optionIconCircle.classList.remove(...OPTION_ICON_CIRCLE_SELECTED_CLASSES);
        optionIconCircle.classList.add(...OPTION_ICON_CIRCLE_DEFAULT_CLASSES);
      }

      if (optionIcon) {
        optionIcon.classList.remove(...OPTION_ICON_SELECTED_CLASSES);
        optionIcon.classList.add(...OPTION_ICON_DEFAULT_CLASSES);
      }

      check?.classList.add("hidden");
    });

    card.classList.add(...SELECTED_CARD_CLASSES);
    const selectedRadio = card.querySelector(".payment-option-selector-radio");
    const selectedCheck = selectedRadio?.querySelector(".payment-option-selector-radio-icon");
    const selectedOptionIcon = card.querySelector(".payment-option-circle-icon");
    const selectedOptionIconCircle = card.querySelector(".payment-option-circle");
    if (selectedRadio) {
      selectedRadio.classList.remove(
        "bg-transparent",
        "border-thm-selector-passive-checkbox-color",
      );
      selectedRadio.classList.add(
        "bg-thm-selector-active-checkbox-color",
        "border-thm-selector-active-checkbox-color",
      );
    }
    if (selectedOptionIcon) {
      selectedOptionIcon.classList.remove(...OPTION_ICON_DEFAULT_CLASSES);
      selectedOptionIcon.classList.add(...OPTION_ICON_SELECTED_CLASSES);
    }
    if (selectedOptionIconCircle) {
      selectedOptionIconCircle.classList.remove(...OPTION_ICON_CIRCLE_DEFAULT_CLASSES);
      selectedOptionIconCircle.classList.add(...OPTION_ICON_CIRCLE_SELECTED_CLASSES);
    }
    selectedCheck?.classList.remove("hidden");

    if (this.hasDirectTransferPanelTarget) {
      this.directTransferPanelTarget.classList.toggle("hidden", this.selectedOption !== "direct-deposit");
    }
  }

  restoreSelectionFromStorage() {
    const sym = sessionStorage.getItem(storage.DIRECT_TRANSFER_SYMBOL);
    const net = sessionStorage.getItem(storage.DIRECT_TRANSFER_NETWORK);
    if (!sym || !net || !this.hasAssetRowTarget) return;
    const row = this.assetRowTargets.find(
      (r) => r.dataset.symbol === sym && r.dataset.networkName === net
    );
    if (!row) return;
    applyAssetRowSelection(row);
    this.selectedRow = row;
    this.applyAssetRowToOverview(row);
    const chainId = row.dataset.chainId || "";
    if (chainId) {
      sessionStorage.setItem(storage.DIRECT_TRANSFER_CHAIN_ID, chainId);
    }
    this.updateCheckoutPaymentMethod(sym, net);
  }

  selectAsset(event) {
    const row = event.currentTarget.closest(
      "[data-payment-options-target='assetRow']"
    );
    if (!row) return;
    if (this.selectedRow === row) return;

    if (this.selectedOption !== "direct-deposit") {
      const directDepositCard = this.optionCardTargets.find(
        (card) => card.dataset.paymentOption === "direct-deposit"
      );
      this.applySelection(directDepositCard);
    }

    this.assetRowTargets.forEach((el) => {
      clearAssetRowSelection(el);
    });

    applyAssetRowSelection(row);
    this.selectedRow = row;

    const symbol = row.dataset.symbol || "";
    const networkName = row.dataset.networkName || "";
    const icon = row.dataset.icon || "";
    const chainId = row.dataset.chainId || "";

    sessionStorage.setItem(storage.DIRECT_TRANSFER_SYMBOL, symbol);
    sessionStorage.setItem(storage.DIRECT_TRANSFER_NETWORK, networkName);
    sessionStorage.setItem(storage.DIRECT_TRANSFER_ICON, icon);
    sessionStorage.setItem(storage.DIRECT_TRANSFER_CHAIN_ID, chainId);
    this.applyAssetRowToOverview(row);
    this.updateCheckoutPaymentMethod(symbol, networkName);

    debugLog("DirectTransferOptions: selected", { symbol, networkName, icon, chainId });

    this.syncContinueDisabled();
  }

  applyAssetRowToOverview(row) {
    this.updateCheckoutOverview(row.dataset.amountDisplay || "");
    this.updateCheckoutFees(
      row.dataset.checkoutFeeDisplay || "",
      row.dataset.checkoutFeeUsd || "",
    );
  }

  /**
   * Restore the overview to its "calculated at payment" state. The overview sits
   * outside the step frame, so its current text may be a stale fee from another
   * step (e.g. connect wallet); restore from the server-rendered data-placeholder.
   */
  resetCheckoutOverviewToPlaceholder() {
    ["checkout_fees", "checkout_total_amount", "checkout_price_mobile"].forEach((id) => {
      const el = document.getElementById(id);
      if (el?.dataset.placeholder) el.textContent = el.dataset.placeholder;
    });
    document
      .querySelectorAll(".js-checkout-fees-incl, .js-checkout-breakdown-total")
      .forEach((el) => {
        if (el.dataset.placeholder) el.textContent = el.dataset.placeholder;
      });
    setCheckoutFeeBreakdownEnabled(false);
  }

  /**
   * Syncs the checkout overview "Estimated Total" display with the selected
   * direct-deposit token amount (e.g. "0.500833 USDC").
   * This updates the persistent overview panel outside the Turbo frame.
   */
  updateCheckoutOverview(amountDisplay) {
    if (!amountDisplay || amountDisplay.trim() === "") return;

    const checkoutTotalAmount = document.getElementById("checkout_total_amount");
    if (checkoutTotalAmount) {
      checkoutTotalAmount.textContent = amountDisplay;
    }

    const checkoutPriceMobile = document.getElementById("checkout_price_mobile");
    if (checkoutPriceMobile) {
      checkoutPriceMobile.textContent = `${amountDisplay}`;
    }

    setCheckoutBreakdownTotal(amountDisplay);
  }

  /**
   * Recomputes the order-summary fee for the selected direct-deposit token. The
   * deposit fee varies by network (origin-chain leg1 gas + the INK legs + reserve),
   * so each option carries its own total; we also keep the breakdown's Network Fee
   * row consistent (total minus the network-independent processing fee).
   */
  updateCheckoutFees(feeDisplay, feeUsdRaw) {
    const display = (feeDisplay || "").trim();
    if (!display) return;

    const checkoutFees = document.getElementById("checkout_fees");
    if (checkoutFees) {
      checkoutFees.textContent = display;
    }

    const inclFee = display.replace(/\s*USD$/i, "");
    document.querySelectorAll(".js-checkout-fees-incl").forEach((el) => {
      el.textContent = `incl. ${inclFee} fees`;
    });

    const total = Number.parseFloat(feeUsdRaw);
    const networkFeeEl = document.querySelector(".js-checkout-network-fee");
    if (networkFeeEl && Number.isFinite(total)) {
      const processing = Number.parseFloat(
        networkFeeEl.dataset.checkoutProcessingFee || "0",
      );
      const networkFee = Math.max(0, total - (Number.isFinite(processing) ? processing : 0));
      document.querySelectorAll(".js-checkout-network-fee").forEach((el) => {
        el.textContent = `$${networkFee.toFixed(2)} USD`;
      });
    }

    setCheckoutFeeBreakdownEnabled(true);
  }

  updateCheckoutPaymentMethod(symbol, networkName) {
    const checkoutPaymentMethod = document.getElementById("checkout_payment_method");
    if (!checkoutPaymentMethod) return;

    const sym = (symbol || "").trim();
    const net = (networkName || "").trim();
    checkoutPaymentMethod.textContent =
      sym && net ? `${net} / ${sym}` : "Awaiting Selection";
  }

  syncContinueDisabled() {
    if (!this.hasContinueButtonTarget) return;
    const enabled =
      this.selectedOption === "third-party" ||
      this.selectedOption === "top-up" ||
      (this.selectedOption === "direct-deposit" && Boolean(this.selectedRow));
    this.continueButtonTarget.classList.toggle("is-disabled", !enabled);
  }

  continue() {
    // CS-2698 DIAG — temporary instrumentation. Snapshot the full picker state
    // at click time so we can tell, post-repro, exactly which exit path fires
    // (third-party / no-row / no-chainId-symbol / fired-Turbo). console.warn
    // (not debugLog) so it shows without PARCEL_DEBUG_MODE. Remove once fixed.
    const diag = {
      selectedOption: this.selectedOption,
      hasSelectedRow: Boolean(this.selectedRow),
      rowChainId: this.selectedRow?.dataset?.chainId || null,
      rowSymbol: this.selectedRow?.dataset?.symbol || null,
      rowNetworkName: this.selectedRow?.dataset?.networkName || null,
      storageChainId: sessionStorage.getItem(storage.DIRECT_TRANSFER_CHAIN_ID),
      storageSymbol: sessionStorage.getItem(storage.DIRECT_TRANSFER_SYMBOL),
      storageNetwork: sessionStorage.getItem(storage.DIRECT_TRANSFER_NETWORK),
      buttonDisabled: this.continueButtonTarget?.classList.contains("is-disabled"),
      sessionValue: this.sessionValue,
      codeValue: this.codeValue,
      assetRowCount: this.assetRowTargets?.length ?? 0,
    };

    if (this.selectedOption === "third-party") {
      this.connect3rdParyWalletHandler();
      return;
    }
    if (this.selectedOption === "top-up") {
      this.handleTopUpContinue();
      return;
    }
    this.handleDirectDepositContinue();
  }

  handleTopUpContinue() {
    const topUpCard = this.optionCardTargets.find(
      (card) => card.dataset.paymentOption === "top-up"
    );
    const topUpURL = topUpCard?.dataset.topUpUrl || "";
    if (!topUpURL) {
      console.warn("PaymentOptions: top-up selected but topUpURL is empty");
      return;
    }
    if (!sessionStorage.getItem(storage.WM_FUNNEL_START_MS)) {
      sessionStorage.setItem(storage.WM_FUNNEL_START_MS, String(Date.now()));
    }
    this.emitTopUpFunnelEvent((topUpCard?.dataset.wmEntry || "").trim());
    this.showLoadingScreen();
    window.requestAnimationFrame(() => {
      window.location.assign(topUpURL);
    });
  }

  /** Maps the wallet-modal entry point to its checkout funnel event. */
  emitTopUpFunnelEvent(wmEntry) {
    const eventByEntry = {
      top_up: "checkout_wm_top_up_clicked",
      dont_have_crypto: "checkout_wm_dont_have_crypto_clicked",
    };
    const event = eventByEntry[wmEntry];
    if (!event) return;
    if (this.sessionValue) {
      sessionStorage.setItem(storage.SESSION, this.sessionValue);
    }
    emitCheckoutEvent(event);
  }

  handleDirectDepositContinue() {
    if (!this.selectedRow) return;
    const chainId =
      this.selectedRow.dataset.chainId ||
      sessionStorage.getItem(storage.DIRECT_TRANSFER_CHAIN_ID) ||
      "";
    const symbol = this.selectedRow.dataset.symbol || "";
    const qs = new URLSearchParams({
      session: this.sessionValue,
      from_dd_options: "1",
      ...(chainId ? { chain_id: String(chainId) } : {}),
      ...(symbol ? { symbol } : {}),
    });
    const url = `/${this.codeValue}/direct-deposit?${qs.toString()}`;
    this.showLoadingScreen();
    Turbo.visit(url, { frame: "step_content", action: "advance" });
  }

  async connect3rdParyWalletHandler() {
    try {
      window.dispatchEvent(
        new CustomEvent("payment-options:listenWalletChange", { bubbles: true })
      );

      await walletService.prepareForNewConnection();
      await walletService.connectWallet();

      const connectedWallet = await walletService.getWalletAddress();
      if (connectedWallet) {
        this.showLoadingScreen();
      }
    } catch (error) {
      debugLog("PaymentOptions: connect wallet failed", error);
      this.dispatch("error", {
        detail: { content: "Unable to open wallet connection. Please refresh and try again." },
      });
    }
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }
}
